import { useRef, useState } from "react";
import { BarChart3, Wallet, Workflow } from "lucide-react";
import { BottomNav } from "../../components/BottomNav";
import { navAnimatedIcons } from "../../components/navAnimatedIcons";
import { useAccentButtonHaptics } from "../../lib/useAccentButtonHaptics";
import { ResourcesList } from "./pages/ResourcesList";
import { ResourceWizard } from "./pages/ResourceWizard";
import { SponsorBalance } from "./pages/Balance";
import { ResourceDetails } from "./pages/ResourceDetails";
import { SponsorStats } from "./pages/Stats";

type View =
  | { kind: "list" }
  | { kind: "create" }
  | { kind: "edit"; resourceId: number }
  | { kind: "view"; resourceId: number }
  | { kind: "balance" }
  | { kind: "stats" }
  | { kind: "resource_stats"; resourceId: number; resourceName?: string };

export default function SponsorMode({ me: _me }: { me: unknown }) {
  const shellRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>(() => initialView());
  useAccentButtonHaptics(shellRef);
  const activeTab =
    view.kind === "balance"
      ? "balance"
      : view.kind === "stats"
        ? "stats"
        : "list";

  return (
    <div className="tp-shell" ref={shellRef}>
      {view.kind === "balance" && <SponsorBalance onBack={() => setView({ kind: "list" })} />}
      {view.kind === "stats" && (
        <SponsorStats />
      )}
      {view.kind === "resource_stats" && (
        <SponsorStats
          resourceId={view.resourceId}
          resourceName={view.resourceName}
          onBack={() => setView({ kind: "list" })}
        />
      )}

      {view.kind === "list" && (
        <ResourcesList
          onCreate={() => setView({ kind: "create" })}
          onEdit={(id) => setView({ kind: "edit", resourceId: id })}
          onView={(id) => setView({ kind: "view", resourceId: id })}
          onStats={(resource) =>
            setView({
              kind: "resource_stats",
              resourceId: resource.id,
              resourceName: resource.name,
            })
          }
        />
      )}

      {view.kind === "create" && (
        <ResourceWizard
          key="sponsor-resource-create"
          mode="create"
          onDone={() => setView({ kind: "list" })}
          onCancel={() => setView({ kind: "list" })}
        />
      )}

      {view.kind === "edit" && (
        <ResourceWizard
          key={`sponsor-resource-edit-${view.resourceId}`}
          mode="edit"
          resourceId={view.resourceId}
          onDone={() => setView({ kind: "list" })}
          onCancel={() => setView({ kind: "list" })}
        />
      )}

      {view.kind === "view" && (
        <ResourceDetails
          resourceId={view.resourceId}
          onBack={() => setView({ kind: "list" })}
        />
      )}

      <BottomNav
        items={[
          {
            key: "list",
            label: "меню",
            icon: Workflow,
            animatedIcon: navAnimatedIcons.home,
            active: activeTab === "list",
            onClick: () => setView({ kind: "list" }),
          },
          {
            key: "stats",
            label: "статистика",
            icon: BarChart3,
            animatedIcon: navAnimatedIcons.stats,
            active: activeTab === "stats",
            onClick: () => setView({ kind: "stats" }),
          },
          {
            key: "balance",
            label: "баланс",
            icon: Wallet,
            active: activeTab === "balance",
            onClick: () => setView({ kind: "balance" }),
            avatar: true,
          },
        ]}
      />
    </div>
  );
}

function initialView(): View {
  const tab = new URLSearchParams(window.location.search).get("tab");
  if (tab === "new-resource") return { kind: "create" };
  if (tab === "topup" || tab === "balance") return { kind: "balance" };
  if (tab === "stats") return { kind: "stats" };
  return { kind: "list" };
}
